import axios from 'axios'
import get from 'lodash/get'
import PracticeOptionsTransformer from 'transformers/PracticeOptionsTransformer'
import PracticeFromFormDataTransformer from 'transformers/PracticeFromFormDataTransformer'
import PracticeLocationsTransformer from '../transformers/PracticeLocationsTransformer'
import ScheduleDatesToCalendarDataTransformer from 'transformers/ScheduleDatesToCalendarDataTransformer'
import ScheduleSlotsToCalendarDataTransformer from 'transformers/ScheduleSlotsToCalendarDataTransformer'
import { applyPracticeUpdateToStore, buildPracticeStoreUpdates } from '~/utils'

export default {
    create(data) {
        return axios.post('practices', PracticeFromFormDataTransformer.transform(data))
    },

    update(id, data, fields) {
        const _fields = (
            fields || [
                'id',
                'name',
                'practiceType',
                'email',
                'phone',
                'mobilePhone',
                'fax',
                'isAgreedToSms',
                'locations(id,name,address,city,state,zip,isAvailabilitySet)',
            ]
        ).join(',')
        return axios
            .put(`practices/${id}`, PracticeFromFormDataTransformer.transform(data), { params: { _fields } })
            .then((practice) => {
                applyPracticeUpdateToStore(buildPracticeStoreUpdates(data, practice))
                return practice
            })
    },

    getMy(fields) {
        const _fields = (
            fields || [
                'id',
                'image',
                'name',
                'practiceType',
                'email',
                'address',
                'city',
                'phone',
                'mobilePhone',
                'isAgreedToSms',
                'fax',
                'state',
                'zip',
                'rating',
                'locations(id,name,address,city,state,zip,isAvailabilitySet)',
                'insurancePlans(id,name)',
                'isAvailabilitySet',
                'masterUserId',
                'specialities',
                'referralsReceivedCount',
                'referralsCreatedCount',
                'facilityServiceTypes(id,name,groupId,facilityServiceTypesGroups(facilityServiceTypesSubgroups(id,name)))',
                'trialPeriodEnds',
                'subscription',
                'billingAddress',
                'activeBillingPeriod',
                'billingEngine',
                'planCode',
                'planStatus',
                'planInterval',
                'currentPeriodEnd',
                'usage',
                'billableUnit',
            ]
        ).join(',')

        return axios
            .get('practices/my', { params: { _fields } })
            .then((practice) => practice)
            .catch((error) => {
                if (get(error, 'response.data.subCode') === 'user.not_practice_member') {
                    return {}
                }

                return Promise.reject(error)
            })
    },

    getPracticeById(practiceId, fields) {
        const _fields = (
            fields || [
                'id',
                'name',
                'image',
                'practiceType',
                'email',
                'address',
                'city',
                'phone',
                'state',
                'zip',
                'locations(id,name,address,city,state,zip,isAvailabilitySet)',
                'insurancePlans(id,name)',
                'isAvailabilitySet',
                'masterUserId',
                'specialities',
                'referralsReceivedCount',
                'referralsCreatedCount',
                'facilityServiceTypes(id,name,groupId,facilityServiceTypesGroups(facilityServiceTypesSubgroups(facilityServiceTests(id,name,serviceTypesSubgroupId,practiceFacilityServiceTypesSubgroup(practiceLocationId))))))',
            ]
        ).join(',')

        return axios.get(`unauthenticated/practices/${practiceId}`, { params: { _fields } })
    },

    getUserLocations(userId, fields) {
        const _fields = (
            fields || [
                'id',
                'name',
                'practiceType',
                'email',
                'address',
                'city',
                'state',
                'zip',
                'locations(id,name,address,city,state,zip,isAvailabilitySet)',
                'isAvailabilitySet',
            ]
        ).join(',')
        return axios.get(`users/${userId}/locations`, {
            params: { _fields },
            transformResponse: [PracticeLocationsTransformer.transform()],
        })
    },

    getPracticeLocations(practiceId, fields) {
        const _fields = (
            fields || [
                'id',
                'name',
                'practiceType',
                'email',
                'address',
                'city',
                'state',
                'zip',
                'locations(id,name,address,city,state,zip,isAvailabilitySet)',
                'isAvailabilitySet',
            ]
        ).join(',')
        return axios
            .get(`practices/${practiceId}/locations`, {
                params: { _fields },
                transformResponse: [PracticeLocationsTransformer.transform()],
            })
            .then((practice) => practice)
            .catch((error) => {
                if (get(error, 'response.data.subCode') === 'user.not_practice_member') {
                    return {}
                }

                return Promise.reject(error)
            })
    },

    getList(query, type, fields) {
        const _fields = (fields || ['id', 'name', 'practiceType', 'city', 'state', 'createdByName', 'masterUserId']).join(',')

        return axios.get('unauthenticated/practices', {
            params: { query, type, _fields },
            transformResponse: [PracticeOptionsTransformer.transform('id', 'name')],
        })
    },

    getPatients(practiceId, query, fields) {
        const _fields = (
            fields || ['id', 'firstName', 'lastName', 'dateOfBirth', 'mobilePhone', 'address', 'zip', 'state', 'city']
        ).join(',')

        return axios.get(`practices/${practiceId}/patients`, {
            params: { query, _fields },
        })
    },

    createMember(data) {
        return axios.post(`practices/${data.practiceId}/members`, data)
    },

    checkPracticeAcceptingInsuranceType({ practiceId, insuranceTypeId }) {
        const _fields = ['practice(id,name,image)', 'insurancePlan(id,name)', 'isAccepting'].join(',')

        return axios.get(`practices/${practiceId}/accepting-insurance/${insuranceTypeId}`, { params: { _fields } })
    },

    getAvailableDates(practiceId, params) {
        return axios.get(`/practices/${practiceId}/availability/dates`, {
            params,
            transformResponse: [ScheduleDatesToCalendarDataTransformer.transform],
        })
    },

    getAvailableSlots(practiceId, params) {
        return axios.get(`/practices/${practiceId}/availability/slots`, {
            params,
            transformResponse: [ScheduleSlotsToCalendarDataTransformer.transform],
        })
    },

    uploadPhoto(file) {
        return axios.post(`/practices/avatar`, { file }).then((response) => {
            if (response.image) {
                applyPracticeUpdateToStore({ image: response.image })
            }
            return response
        })
    },

    removePhoto() {
        return axios.delete(`/practices/avatar`).then((response) => {
            applyPracticeUpdateToStore({ image: null })
            return response
        })
    },

    setupIntent() {
        return axios.post(`/billing/practices/setup-intent`)
    },

    createSubscription(data) {
        return axios.post(`/billing/practices/create-subscription`, data)
    },

    getPlans() {
        return axios.get('/billing/plans')
    },

    subscribePlan(data) {
        return axios.post('/billing/practices/subscribe', data)
    },

    changePlan(data) {
        return axios.post('/billing/practices/change-plan', data)
    },

    downgradeToFree() {
        return axios.post('/billing/practices/downgrade-to-free')
    },

    getPlanUsage() {
        return axios.get('/billing/practices/usage')
    },

    saveBillingAddress(data) {
        return axios.post('/billing/practices/billing-address', data)
    },

    getEstimatedPrice() {
        return axios.get(`/billing/practices/get-estimated-prices`)
    },

    getInvoicesData() {
        return axios.get('/billing/practices/get-invoices')
    },

    cancelSubscription() {
        return axios.post('/billing/practices/cancel-subscription')
    },

    getPracticeCards() {
        return axios.get('/billing/practices/get-paying-cards-list')
    },

    setDefaultCard(paymentId) {
        return axios.post('/billing/practices/set-default-payment-card', { paymentId })
    },

    deleteCard(paymentId) {
        return axios.post('/billing/practices/delete-payment-method', { paymentId })
    },

    acceptInsuranceAndTests(practiceId, insurancePlanIds, testIds) {
        return axios.post(`/practices/${practiceId}/accept-insurance-and-tests`, { insurancePlanIds, testIds })
    },

    createFromNpi(data) {
        return axios.post('unauthenticated/practices', data)
    },
}
